import styled from '@emotion/styled';
import { TestId } from '@components/test-ids';
import { Grid } from '@mui/material';
import { withTheme } from '@emotion/react';
import { Theme, ThemeProps } from '@theme/base';
import { Icon } from '@components/common/generic-icon';
import { RippleCard } from '@components/common/ripple-card';
import { LazyLoadImage } from '@components/common/lazy-load';
import { boxShadowMixins } from '@styles/mixins';
import { mq, getSizes } from '@styles/breakpoints';

/* ***************** *
 *       Types       *
 * ***************** */

export interface TripTypesGridProps {
  tripTypes: Array<TripTypeGridItem>;
  images: Array<string>;
  testId?: string;
  theme: Theme;
}

export interface TripTypeGridItem {
  renderLink: (children: JSX.Element) => JSX.Element;
  title: string;
  icon?: string;
}

/* ****************** *
 *       Styles       *
 * ****************** */

const Container = styled(Grid)(({ theme }: ThemeProps) => ({
  paddingTop: theme.custom.spacing.xLarge,
  [mq.small]: {
    paddingTop: 0
  },

  ['.MuiGrid-item']: {
    paddingTop: theme.custom.spacing.medium,
    [mq.small]: {
      paddingTop: theme.custom.spacing.medium * 2
    }
  }
}));

const TripTypeLinkItem: any = styled.div(({ theme }: ThemeProps) => ({
  ...(theme.custom.typography.labels.responsive as any),
  display: 'flex',
  alignItems: 'center',
  border: `1px solid ${theme.custom.colors.group10.light}`,
  borderRadius: 6,
  boxShadow: boxShadowMixins(theme.custom.colors.group10.light).default,
  color: theme.custom.colors.group4.base,
  cursor: 'pointer',
  flexDirection: 'row',
  fontWeight: 600,
  maxHeight: 75,
  overflow: 'hidden',
  textAlign: 'center',

  ['img']: {
    height: 80,
    display: 'inline-block',
    width: 100,
    [mq.small]: {
      height: 'unset',
      width: '100%'
    }
  },

  [mq.small]: {
    flexDirection: 'column',
    height: '100%',
    fontSize: theme.custom.fontSize.medium,
    maxHeight: 'unset'
  },

  ['i']: {
    fontSize: '2rem',
    color: theme.custom.colors.group4.base,
    marginRight: theme.custom.spacing.small,
    [mq.small]: {
      fontSize: '2.4rem',
      marginBottom: 0,
      marginRight: theme.custom.spacing.xSmall
    }
  },

  ['&:hover, &:hover i']: {
    color: theme.custom.colors.group2.base
  },

  ['&:hover']: {
    boxShadow: boxShadowMixins(theme.custom.colors.group10.base).default
  }
}));

const TitleContainer: any = styled.div(({ theme }: ThemeProps) => ({
  alignItems: 'center',
  display: 'flex',
  flexDirection: 'row',
  height: '100%',
  justifyContent: 'center',
  paddingLeft: theme.custom.spacing.large,
  padding: theme.custom.spacing.medium,

  [mq.small]: {
    paddingLeft: theme.custom.spacing.medium
  }
}));

const ImageWrapper = styled.div({
  alignItems: 'center',
  display: 'flex',
  flexShrink: 0,
  maxHeight: 240,
  overflow: 'hidden'
});

const TripTypeImage: any = styled(LazyLoadImage)({
  WebkitUserSelect: 'none',
  WebkitTouchCallout: 'none'
});

export const TripTypesGridComponent = ({ testId, theme, tripTypes, images }: TripTypesGridProps) => {
  /* *** RENDERERS *** */
  const renderTripTypeItems = () => {
    return tripTypes.map((tripType: TripTypeGridItem, idx: number) => {
      const imageSizes = getSizes([20], Math.round(100 / 3));

      return (
        <Grid key={tripType.title} item xs={12} sm={12 / 3}>
          {tripType.renderLink(
            <RippleCard style={{ borderRadius: 6 }}>
              <TripTypeLinkItem data-testid={TestId.tripTypesGrid.item}>
                <ImageWrapper>
                  <TripTypeImage src={images[idx]} width={294} height={240} sizes={imageSizes} />
                </ImageWrapper>
                <TitleContainer>
                  {tripType.icon && <Icon name={tripType.icon} />}
                  {tripType.title}
                </TitleContainer>
              </TripTypeLinkItem>
            </RippleCard>
          )}
        </Grid>
      );
    });
  };

  return (
    <Container container spacing={theme.custom.gridSpacing.default} data-testid={testId || TestId.tripTypesGrid.main}>
      {renderTripTypeItems()}
    </Container>
  );
};

export const TripTypesGrid = withTheme(TripTypesGridComponent);
